import {
  useImportHubspotOnboardingDeal,
  useListHubspotOnboardingRuns,
  useScanHubspotOnboarding,
} from '@actions';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import Button from '@components/V4/Button';
import Input from '@components/V4/Inputs/Input';
import { RefreshIcon, SearchIcon } from '@heroicons/react/outline';
import { EmptyState, SectionHeading, TableShell } from '@modules/Admin/Reporting/common';
import {
  HubspotOnboardingRunDTO,
  HubspotOnboardingRunStatus,
} from '@shared/apis/hubspotOnboarding.apis';
import useToast from 'apps/agora/src/hooks/useToast';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import moment from 'moment';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';

const STATUS_STYLES: Record<HubspotOnboardingRunStatus, string> = {
  running: 'bg-customGold/15 text-customGold',
  imported: 'bg-customGreen/15 text-customGreen',
  skipped: 'bg-white/10 text-customGrey',
  failed: 'bg-customRed/15 text-customRed',
};

const errorMessage = (error: unknown, fallback: string) =>
  (error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
    ?.message ?? fallback;

const RunRow = (props: {
  run: HubspotOnboardingRunDTO;
  onRetry: (dealId: string) => void;
  retrying: boolean;
}) => {
  const { run, onRetry, retrying } = props;
  const history = useHistory();
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="border-b border-white/5 align-top last:border-b-0">
        <td className="px-4 py-2.5 text-left">
          <span
            className={mergeClassNames(
              'rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase',
              STATUS_STYLES[run.status]
            )}
          >
            {run.status}
          </span>
          <span className="block text-[11px] text-customGrey">
            {run.source} · attempt {run.attempts} · {moment(run.startedAt).format('D MMM HH:mm')}
          </span>
        </td>
        <td className="px-4 py-2.5 text-left text-customLight">
          <a
            href={`https://app.hubspot.com/contacts/deal/${run.dealId}`}
            target="_blank"
            rel="noreferrer"
            className="text-customPrimary hover:underline"
          >
            {run.dealName ?? run.dealId}
          </a>
          <span className="block text-[11px] text-customGrey">{run.dealId}</span>
        </td>
        <td className="px-4 py-2.5 text-left text-customLight">
          {run.studentId ? (
            <button
              type="button"
              className="text-left font-semibold text-white hover:underline"
              onClick={() => history.push(`/admin/case-tracker?student=${run.studentId}`)}
            >
              {run.studentName}
            </button>
          ) : (
            run.studentName ?? '—'
          )}
          <span className="block text-[11px] text-customGrey">
            {run.studentEmail ?? '—'}
            {run.parentEmail ? ` · parent ${run.parentEmail}` : ''}
          </span>
        </td>
        <td className="px-4 py-2.5 text-left text-customLight">
          {run.enrollments.length
            ? `${run.enrollments.filter((item) => item.enrollmentId).length}/${
                run.enrollments.length
              } created`
            : '—'}
          {run.contractAmount ? (
            <span className="block text-[11px] text-customGrey">
              contract {run.contractAmount} {run.contractCurrency}
            </span>
          ) : null}
        </td>
        <td className="px-4 py-2.5 text-left text-xs text-customGrey">
          {run.error ? <span className="text-customRed">{run.error}</span> : null}
          {run.warnings.length ? (
            <button
              type="button"
              className="block text-customGold hover:underline"
              onClick={() => setOpen((value) => !value)}
            >
              {run.warnings.length} note{run.warnings.length === 1 ? '' : 's'} {open ? '▴' : '▾'}
            </button>
          ) : null}
        </td>
        <td className="px-4 py-2.5 text-right">
          {run.status !== 'running' ? (
            <Button
              variant="secondary"
              buttonText={run.status === 'imported' ? 'Import again' : 'Retry'}
              disabled={retrying}
              onClick={() => onRetry(run.dealId)}
            />
          ) : null}
        </td>
      </tr>
      {open ? (
        <tr className="border-b border-white/5">
          <td colSpan={6} className="px-4 pb-3 text-xs text-customLight">
            <ul className="list-disc pl-5">
              {run.warnings.map((warning, index) => (
                <li key={index}>{warning}</li>
              ))}
              {run.enrollments.map((item, index) => (
                <li key={`e-${index}`} className={item.error ? 'text-customGold' : ''}>
                  {item.courseTitle}: {item.sessions} sessions
                  {item.startDate ? ` · ${moment(item.startDate).format('D MMM YYYY')}` : ''}
                  {item.endDate ? ` → ${moment(item.endDate).format('D MMM YYYY')}` : ''}
                  {item.error ? ` · ${item.error}` : ' · created'}
                </li>
              ))}
            </ul>
          </td>
        </tr>
      ) : null}
    </>
  );
};

const HubspotOnboarding = () => {
  const toast = useToast();
  const {
    data: runs,
    isLoading,
    refetch,
    isFetching,
  } = useListHubspotOnboardingRuns({ refetchOnWindowFocus: false, refetchInterval: 15000 });
  const [dealId, setDealId] = useState('');
  const [retryingId, setRetryingId] = useState<string>();

  const { mutate: scan, isLoading: isScanning } = useScanHubspotOnboarding({
    onSuccess: (result) =>
      toast.success(
        `${result.pipeline} / ${result.stage}: ${result.dealsInStage} deals, ${result.queued} processed now, ${result.alreadyProcessed} already handled.`
      ),
    onError: (error: unknown) => toast.error(errorMessage(error, 'The scan failed.')),
  });
  const { mutate: importDeal, isLoading: isImporting } = useImportHubspotOnboardingDeal({
    onSuccess: (run) => {
      setRetryingId(undefined);
      setDealId('');
      toast[run.status === 'imported' ? 'success' : 'error'](
        run.status === 'imported'
          ? `Imported ${run.studentName ?? run.dealId}.`
          : run.error ?? `Run ended with status ${run.status}.`
      );
    },
    onError: (error: unknown) => {
      setRetryingId(undefined);
      toast.error(errorMessage(error, 'The import failed.'));
    },
  });

  const retry = (id: string) => {
    setRetryingId(id);
    importDeal({ dealId: id, force: true });
  };

  return (
    <div className="flex flex-col gap-6 p-8 font-raleway text-white">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold">HubSpot Onboarding</h1>
          <span className="max-w-3xl text-xs text-customGrey">
            Deals that reach "Pending ONB" in the Onboarding Pipeline are imported: student account
            from the associated contacts, enrollments from Supabase by email, contract from the
            Sales and Contracts deals, then the activation emails. A scan runs every 10 minutes when
            enabled; use the buttons to scan or import on demand.
          </span>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="w-56">
            <Input
              type="text"
              id="deal-id"
              name="dealId"
              label="Import a deal by id"
              placeholder="HubSpot deal id"
              value={dealId}
              onChange={(event) => setDealId(event.target.value.trim())}
            />
          </div>
          <Button
            variant="secondary"
            buttonText="Import"
            isLoading={isImporting && !retryingId}
            disabled={!/^\d+$/.test(dealId) || isImporting}
            onClick={() => importDeal({ dealId, force: true })}
          />
          <Button
            buttonText={isScanning ? 'Scanning…' : 'Scan Pending ONB now'}
            icon={<SearchIcon className="h-4 w-4" />}
            isLoading={isScanning}
            disabled={isScanning}
            onClick={() => scan({})}
          />
          <Button
            variant="secondary"
            buttonText="Refresh"
            icon={<RefreshIcon className="h-4 w-4" />}
            isLoading={isFetching}
            onClick={() => refetch()}
          />
        </div>
      </div>

      <SectionHeading
        title={`Runs (${runs?.length ?? 0})`}
        description="Newest first. Open the notes to see what was created and what needs a hand."
      />
      {isLoading ? (
        <div className="flex h-48 items-center justify-center">
          <LoadingSpinner />
        </div>
      ) : !runs?.length ? (
        <EmptyState>No imports yet. Run a scan or paste a deal id.</EmptyState>
      ) : (
        <TableShell minWidth="min-w-[1000px]">
          <thead>
            <tr className="border-b border-white/10 text-xs uppercase tracking-wide text-customGrey">
              <th className="px-4 py-2.5 text-left font-semibold">Status</th>
              <th className="px-4 py-2.5 text-left font-semibold">Deal</th>
              <th className="px-4 py-2.5 text-left font-semibold">Student</th>
              <th className="px-4 py-2.5 text-left font-semibold">Enrollments</th>
              <th className="px-4 py-2.5 text-left font-semibold">Notes</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {runs.map((run) => (
              <RunRow
                key={run._id}
                run={run}
                onRetry={retry}
                retrying={isImporting && retryingId === run.dealId}
              />
            ))}
          </tbody>
        </TableShell>
      )}
    </div>
  );
};

export default HubspotOnboarding;
